import { Button, InfoTip, SearchCombobox, TextInput, type ComboOption } from '@zarya/ui';
import { t } from '@app/i18n';
import { CAUSAS, causaVocab, type Causa } from './causa';
import {
  PERIODOS,
  periodFitsWindow,
  periodLabel,
  windowLabel,
  type PeriodID,
} from './Decisions.api';
import type { Decision } from './vocabulario';
import s from './Decisions.module.css';

/**
 * Filtros de T16 (FR-096). Período, identidade e tool vão **para a fonte**
 * (o middleware); veredito filtra a página no backend. A busca por pessoa e
 * `request_id` é local e mora na barra da tabela, com a indicação no campo.
 */

export type VerdictFilter = Decision['verdict'] | 'todos';

/**
 * Opções do filtro por causa. Combobox e não pílulas: são doze causas, e a
 * fileira de pílulas quebraria em três linhas antes do primeiro filtro útil.
 *
 * O rótulo aqui é o `filtro` do catálogo, não o `rotulo` do chip: as três causas
 * de token compartilham «token» na tabela (de propósito — é o que o operador
 * precisa ver de relance) e numa lista de opções isso seriam três linhas
 * idênticas e inescolhíveis.
 */
const causeOptions = (): ComboOption[] =>
  CAUSAS.map((causa) => ({
    value: causa,
    label: causaVocab(causa).filtro,
    hint: causa,
  }));

export interface DecisionsFiltersValue {
  periodo: PeriodID;
  /** Início da janela quando `periodo === 'custom'` (valor de `datetime-local`). */
  desde?: string;
  veredito: VerdictFilter;
  /** Causa do contrato (`DecisionCause`); `null` = todas. */
  cause: Causa | null;
  identityId: string | null;
  tool: string;
  /** Inclui as decisões do Simulador (T18) e da lente «Ver como» (T19). */
  includeSimulated: boolean;
  /** Inclui as chamadas de controle do executor (D1, aim-6ph.8). */
  includeControlCalls: boolean;
}

export interface DecisionsFiltersProps {
  valor: DecisionsFiltersValue;
  onChange: (valor: DecisionsFiltersValue) => void;
  /** Identidades do combobox; vazio enquanto carrega. */
  identidades: ComboOption[];
  /**
   * A janela que a fonte cobre, em minutos (`window_max_min` da página).
   * `null`/ausente = ainda não se sabe, e aí todos os períodos são oferecidos —
   * ver `periodFitsWindow`.
   */
  windowMaxMin?: number | null;
}

/** Função: o rótulo vem do catálogo, e uma lista de módulo congela o idioma. */
const verdictOptions = (): { id: VerdictFilter; rotulo: string }[] => [
  { id: 'todos', rotulo: t('decisions.filters.verdict.all') },
  { id: 'permitido', rotulo: t('decisions.verdict.permitido') },
  { id: 'negado', rotulo: t('decisions.verdict.negado') },
  { id: 'indeterminado', rotulo: t('decisions.verdict.indeterminado') },
];

/** Pílula de filtro (`aria-pressed`), como no protótipo. */
function Pilula({
  ativa,
  onClick,
  children,
  disabled,
  title,
}: {
  ativa: boolean;
  onClick: () => void;
  children: string;
  /**
   * Desabilitada é diferente de ausente, e é a escolha certa aqui: sumir com a
   * pílula «7 d» faria parecer que a tela nunca ofereceu a semana. Desabilitada
   * com o motivo diz o que é verdade — a fonte não cobre tanto — e some sozinha
   * quando a retenção crescer.
   */
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={[s.pilula, ativa ? s.pilulaOn : ''].filter(Boolean).join(' ')}
      aria-pressed={ativa}
      disabled={disabled}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function DecisionsFilters({
  valor,
  onChange,
  identidades,
  windowMaxMin,
}: DecisionsFiltersProps) {
  const clean =
    valor.periodo === '1h' &&
    valor.veredito === 'todos' &&
    valor.cause === null &&
    valor.identityId === null &&
    valor.tool === '' &&
    !valor.includeSimulated &&
    !valor.includeControlCalls;

  return (
    <div className={s.filtros}>
      <div className={s.grupo} role="group" aria-label={t('decisions.filters.period.label')}>
        <span className={s.grupoRotulo}>{t('decisions.filters.period.label')}</span>
        {PERIODOS.map((period) => {
          // DEF-35: a tela oferece só o que a fonte cobre. Oferecer «7 d» sobre
          // uma janela de 24 h devolvia 24 h com um aviso discreto, e a leitura
          // natural daquilo é «não houve nada na semana» — o oposto de «não
          // perguntamos pela semana».
          const fits = periodFitsWindow(period.id, windowMaxMin);
          return (
            <Pilula
              key={period.id}
              ativa={valor.periodo === period.id}
              disabled={!fits}
              title={
                fits
                  ? undefined
                  : t('decisions.filters.period.beyondWindow', {
                      window: windowLabel(windowMaxMin),
                    })
              }
              onClick={() => onChange({ ...valor, periodo: period.id })}
            >
              {periodLabel(period.id)}
            </Pilula>
          );
        })}
        {valor.periodo === 'custom' ? (
          <TextInput
            type="datetime-local"
            aria-label={t('decisions.filters.since.label')}
            value={valor.desde ?? ''}
            onChange={(e) => onChange({ ...valor, desde: e.target.value })}
          />
        ) : null}
      </div>

      <div className={s.grupo} role="group" aria-label={t('decisions.filters.verdict.label')}>
        <span className={s.grupoRotulo}>{t('decisions.filters.verdict.label')}</span>
        {verdictOptions().map((veredito) => (
          <Pilula
            key={veredito.id}
            ativa={valor.veredito === veredito.id}
            onClick={() => onChange({ ...valor, veredito: veredito.id })}
          >
            {veredito.rotulo}
          </Pilula>
        ))}
      </div>

      <div className={s.campo}>
        <span className={s.campoRotulo} aria-hidden="true">
          {t('decisions.filters.identity.label')}
        </span>
        <SearchCombobox
          label={t('decisions.filters.identity.label')}
          size="sm"
          clearable
          options={identidades}
          value={valor.identityId}
          placeholder={t('decisions.filters.identity.placeholder')}
          emptyMessage={t('decisions.filters.emptyOption')}
          onChange={(identityId) => onChange({ ...valor, identityId })}
        />
      </div>

      <div className={s.campo}>
        <span className={s.campoRotulo} aria-hidden="true">
          {t('decisions.filters.cause.label')}
        </span>
        <SearchCombobox
          label={t('decisions.filters.cause.label')}
          size="sm"
          clearable
          options={causeOptions()}
          value={valor.cause ?? null}
          placeholder={t('decisions.filters.cause.placeholder')}
          emptyMessage={t('decisions.filters.emptyOption')}
          onChange={(cause) => onChange({ ...valor, cause: (cause as Causa) ?? null })}
        />
      </div>

      <div className={s.campo}>
        <label className={s.campoRotulo} htmlFor="filtro-tool">
          {t('decisions.filters.tool.label')}
        </label>
        <TextInput
          id="filtro-tool"
          mono
          placeholder={t('decisions.filters.tool.placeholder')}
          value={valor.tool}
          onChange={(e) => onChange({ ...valor, tool: e.target.value })}
        />
      </div>

      {/* `grupo` e não `campo`: `campo` é coluna (rótulo em cima do controle) e
          empilharia o ⓘ debaixo do switch. Aqui os dois são uma linha só. */}
      <div className={s.grupo}>
        <label className={s.toggleSimulacoes}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={valor.includeSimulated}
            checked={valor.includeSimulated}
            onChange={(e) => onChange({ ...valor, includeSimulated: e.target.checked })}
          />
          <span>{t('decisions.filters.includeSimulated')}</span>
        </label>
        <InfoTip
          text={t('decisions.filters.includeSimulated.tip')}
          label={t('decisions.filters.includeSimulated.label')}
          size="xs"
        />
      </div>

      <div className={s.grupo}>
        <label className={s.toggleSimulacoes}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={valor.includeControlCalls}
            checked={valor.includeControlCalls}
            onChange={(e) => onChange({ ...valor, includeControlCalls: e.target.checked })}
          />
          <span>{t('decisions.filters.includeControlCalls')}</span>
        </label>
        <InfoTip
          text={t('decisions.filters.includeControlCalls.tip')}
          label={t('decisions.filters.includeControlCalls.label')}
          size="xs"
        />
      </div>

      {!clean ? (
        <Button
          variant="soft"
          size="sm"
          icon="close"
          onClick={() =>
            onChange({
              periodo: '1h',
              veredito: 'todos',
              cause: null,
              identityId: null,
              tool: '',
              includeSimulated: false,
              includeControlCalls: false,
            })
          }
        >
          {t('decisions.filters.clear')}
        </Button>
      ) : null}
    </div>
  );
}
